import { useCallback, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Library, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { fetchVocabularies, type VocabularyInfoDto } from '@/api/vocabularies';
import { VocabularyTermPicker } from '@/components/vocabulary/VocabularyTermPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { PageHeader } from '@/components/shell/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const VOCABULARIES_QUERY_KEY = ['vocabularies', 'catalogue'] as const;
const STALE_TIME_MS = 60_000;

function isMeaningful(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== '' && value !== 'unknown';
}

function formatRelative(iso: string | null | undefined): string | null {
  if (!isMeaningful(iso)) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const deltaSec = Math.floor((Date.now() - ms) / 1000);
  if (deltaSec < 60) return `${String(deltaSec)}s ago`;
  if (deltaSec < 3600) return `${String(Math.floor(deltaSec / 60))}m ago`;
  if (deltaSec < 86_400) return `${String(Math.floor(deltaSec / 3600))}h ago`;
  return `${String(Math.floor(deltaSec / 86_400))}d ago`;
}

function shortSha(sha: string | null | undefined): string | null {
  return isMeaningful(sha) ? sha.slice(0, 12) : null;
}

function fileName(url: string): string {
  try {
    const path = new URL(url).pathname;
    return path.slice(path.lastIndexOf('/') + 1) || url;
  } catch {
    return url;
  }
}

export function AdminVocabulariesPage(): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: VOCABULARIES_QUERY_KEY,
    queryFn: ({ signal }) => fetchVocabularies(signal),
    staleTime: STALE_TIME_MS,
  });

  const handleRefresh = useCallback(() => {
    query.refetch().catch(() => undefined);
  }, [query]);

  return (
    <>
      <PageHeader
        icon={<Library className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.vocabularies.heading')}
        subtitle={t('routes.admin.vocabularies.subheading')}
        actions={
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={query.isFetching}>
            <RefreshCw
              className={cn('h-4 w-4', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
            {t('routes.admin.vocabularies.refresh')}
          </Button>
        }
      />
      <div className="px-8 py-5 space-y-4 bg-background">
        {query.isPending && <CatalogueSkeleton />}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t('routes.admin.vocabularies.error.heading')}
              message={t('routes.admin.vocabularies.error.message')}
              codeLabel={t('routes.admin.vocabularies.error.codeLabel')}
              onRetry={handleRefresh}
              retryLabel={t('routes.admin.vocabularies.error.retry')}
            />
          </div>
        )}

        {query.isSuccess &&
          (query.data.length === 0 ? (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.vocabularies.empty')}
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {query.data.map((vocab) => (
                <VocabularyCard key={vocab.id} vocab={vocab} />
              ))}
            </div>
          ))}
      </div>
    </>
  );
}

function VocabularyCard({ vocab }: { readonly vocab: VocabularyInfoDto }): ReactElement {
  const { t } = useTranslation();
  const indexedAgo = formatRelative(vocab.indexedAt);
  const vocabName = vocab.name ?? vocab.id.toUpperCase();
  return (
    <Card className="flex flex-col">
      <div className="px-6 pt-5">
        <VocabularyTermPicker
          vocabularyId={vocab.id}
          display={vocab.display ?? null}
          showInfo
          dense
          vocabularyName={vocab.name ?? null}
          version={vocab.dataVersion ?? null}
          placeholder={t('routes.admin.vocabularies.search.placeholder', { name: vocabName })}
          ariaLabel={t('routes.admin.vocabularies.search.ariaLabel', { name: vocabName })}
        />
      </div>
      <CardHeader className="pt-5 pb-3">
        <div className="flex items-start justify-between gap-2 min-h-[2.75rem]">
          <CardTitle className="text-base leading-snug line-clamp-2">{vocabName}</CardTitle>
          <Badge variant="secondary" className="font-mono text-[11px] uppercase shrink-0">
            {vocab.id}
          </Badge>
        </div>
        <CardDescription className="mt-1">
          {(() => {
            // Prefer the dated S3 snapshot (consistent across vocabularies); fall back to the
            // source-derived data version, then to "unknown".
            const date = isMeaningful(vocab.snapshotDate)
              ? vocab.snapshotDate
              : isMeaningful(vocab.dataVersion)
                ? vocab.dataVersion
                : null;
            return date !== null
              ? t('routes.admin.vocabularies.card.snapshot', { date })
              : t('routes.admin.vocabularies.card.versionUnknown');
          })()}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 pt-0 text-sm">
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
          <Field label={t('routes.admin.vocabularies.card.terms')}>
            {vocab.termCount.toLocaleString()}
          </Field>
          <Field label={t('routes.admin.vocabularies.card.idPrefix')}>
            <span className="font-mono text-xs">
              {isMeaningful(vocab.idPrefix) ? vocab.idPrefix : '—'}
            </span>
          </Field>
          <Field label={t('routes.admin.vocabularies.card.indexed')}>
            <span title={vocab.indexedAt ?? undefined}>{indexedAgo ?? '—'}</span>
          </Field>
        </dl>

        {isMeaningful(vocab.sourceUrl) ? (
          <SourceRow
            label={t('routes.admin.vocabularies.card.source')}
            url={vocab.sourceUrl}
            sha={vocab.sourceSha256}
          />
        ) : (
          <p className="min-w-0 text-xs text-muted-foreground">
            <span>{t('routes.admin.vocabularies.card.source')}: </span>
            {'—'}
          </p>
        )}

        {/* Always render the annotations block so the section aligns across cards in a row. */}
        <div className="space-y-1.5 border-t border-border pt-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('routes.admin.vocabularies.card.annotations')}
          </p>
          {vocab.annotations !== null && vocab.annotations !== undefined && vocab.annotations.length > 0 ? (
            vocab.annotations.map((a) => (
              <SourceRow
                key={a.type}
                label={t(`routes.admin.vocabularies.annotationType.${a.type}`, { defaultValue: a.type })}
                url={a.sourceUrl}
                sha={a.sha256}
              />
            ))
          ) : (
            <p className="text-xs text-muted-foreground">—</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium">{children}</dd>
    </div>
  );
}

function SourceRow({
  label,
  url,
  sha,
}: {
  readonly label: string;
  readonly url: string;
  readonly sha: string | null | undefined;
}): ReactElement {
  const sha12 = shortSha(sha);
  return (
    <div className="min-w-0 text-xs">
      <span className="text-muted-foreground">{label}: </span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all"
        title={url}
      >
        {fileName(url)}
      </a>
      {sha12 !== null && (
        <span className="ml-1 font-mono text-[10px] text-muted-foreground" title={sha ?? undefined}>
          ({sha12})
        </span>
      )}
    </div>
  );
}

function CatalogueSkeleton(): ReactElement {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-44 w-full" />
    </div>
  );
}
